import { useMemo, useState } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  PORTAL_ICON_OPTIONS,
  PORTAL_ICON_CATEGORIES,
  getPortalIconByName,
  getPortalIconLabel,
} from '../../utils/portal/iconOptions';

const PortalIconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const SelectedIcon = getPortalIconByName(value);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return PORTAL_ICON_OPTIONS;
    return PORTAL_ICON_OPTIONS.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query) ||
        opt.category.toLowerCase().includes(query)
    );
  }, [search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((opt) => {
      if (!map[opt.category]) map[opt.category] = [];
      map[opt.category].push(opt);
    });
    return PORTAL_ICON_CATEGORIES.filter((cat) => map[cat]?.length).map((cat) => ({
      category: cat,
      icons: map[cat],
    }));
  }, [filtered]);

  return (
    <div className="relative">
      <label className="text-xs font-semibold uppercase text-gray-500">Icon</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="input-modern mt-1 py-1.5 w-full flex items-center justify-between gap-3 text-left"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center shrink-0">
            <SelectedIcon className="w-4.5 h-4.5 text-primary-600 dark:text-primary-400" />
          </span>
          <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
            {getPortalIconLabel(value)}
          </span>
        </span>
        {open ? <FaChevronUp className="w-3 h-3 text-gray-400 shrink-0" /> : <FaChevronDown className="w-3 h-3 text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="mt-2 rounded-2xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 shadow-lg p-3 space-y-3 z-20">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons..."
              className="input-modern pl-9 py-2 text-sm"
            />
          </div>

          <div className="max-h-56 overflow-y-auto custom-scrollbar space-y-3 pr-1">
            {grouped.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No icons match your search.</p>
            ) : (
              grouped.map(({ category, icons }) => (
                <div key={category}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">{category}</p>
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
                    {icons.map((opt) => {
                      const Icon = opt.Icon;
                      const selected = value === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          title={opt.label}
                          onClick={() => {
                            onChange(opt.value);
                            setOpen(false);
                            setSearch('');
                          }}
                          className={`p-2 rounded-xl border transition-all flex items-center justify-center ${
                            selected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 ring-2 ring-primary-500/30'
                              : 'border-gray-100 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalIconPicker;
